import { ApiState, IApiState } from '../../shared/models/api-state.model';
import { ActionReducer, createFeature, createReducer, createSelector, MemoizedSelector, on } from '@ngrx/store';
import { IScorecardPreview } from '../../shared/models/scorecard-preview.model';
import {
  SCORECARD_CHALLENGE_API_ACTIONS,
  SCORECARD_DETAILS_API_ACTIONS,
  SCORECARD_PREVIEW_API_ACTIONS,
  STATEMENT_CHALLENGE_UPDATE_API_ACTIONS,
  STATEMENT_CREATE_CHALLENGE_API_ACTIONS,
} from '../actions';
import { IScorecard } from '../../shared/models/scorecard.model';
import { ScorecardDetailsNormalizedModel } from '../../shared/models/scorecard-details-normalized.model';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import {
  IStatementChallengeUpdateRequest,
  IStatementChallengeCreateRequest,
  TScorecardChallengeOperation,
} from '../../shared/interfaces/challenge.interface';
import { IStatement } from '../../shared/models/scorecard-section.model';

const SCORECARD_STATE_KEY = 'scorecard';

export interface IStatementChallengeState extends EntityState<IApiState<IStatementChallengeCreateRequest>> {}

export interface IStatementChallengeUpdatingState extends EntityState<IApiState<IStatementChallengeUpdateRequest>> {}

export interface IScorecardState {
  scorecardPreview: IApiState<IScorecardPreview[]>;
  scorecardDetails: IApiState<IScorecard>;
  statementChallenge: IStatementChallengeState;
  statementChallengeUpdating: IStatementChallengeUpdatingState;
  scorecardChallenge: IApiState<TScorecardChallengeOperation>;
}

export const statementChallengeAdapter: EntityAdapter<IApiState<IStatementChallengeCreateRequest>> =
  createEntityAdapter<IApiState<IStatementChallengeCreateRequest>>({
    selectId: ({ data }) => data!.statementId,
  });

export const statementChallengeUpdatingAdapter: EntityAdapter<IApiState<IStatementChallengeUpdateRequest>> =
  createEntityAdapter<IApiState<IStatementChallengeUpdateRequest>>({
    selectId: ({ data }) => data!.id,
  });

export const initialState: IScorecardState = {
  scorecardPreview: new ApiState<IScorecardPreview[]>(),
  scorecardDetails: new ApiState<IScorecard>(),
  statementChallenge: statementChallengeAdapter.getInitialState(),
  statementChallengeUpdating: statementChallengeUpdatingAdapter.getInitialState(),
  scorecardChallenge: new ApiState<TScorecardChallengeOperation>(),
};

const reducer: ActionReducer<IScorecardState> = createReducer<IScorecardState>(
  initialState,
  on(
    SCORECARD_PREVIEW_API_ACTIONS.get,
    (state): IScorecardState => ({
      ...state,
      scorecardPreview: state.scorecardPreview.load(),
    }),
  ),
  on(
    SCORECARD_PREVIEW_API_ACTIONS.error,
    (state, { error }): IScorecardState => ({
      ...state,
      scorecardPreview: state.scorecardPreview.setError(error),
    }),
  ),
  on(
    SCORECARD_PREVIEW_API_ACTIONS.success,
    (state, { data }): IScorecardState => ({
      ...state,
      scorecardPreview: state.scorecardPreview.success(data),
    }),
  ),

  on(SCORECARD_DETAILS_API_ACTIONS.get, (state) => ({
    ...state,
    scorecardDetails: state.scorecardDetails.load(),
    scorecardChallenge: new ApiState(),
  })),
  on(SCORECARD_DETAILS_API_ACTIONS.success, (state, { data }) => ({
    ...state,
    scorecardDetails: state.scorecardDetails.success(data),
  })),
  on(SCORECARD_DETAILS_API_ACTIONS.error, (state, { error }) => ({
    ...state,
    scorecardDetails: state.scorecardDetails.setError(error),
  })),

  on(STATEMENT_CREATE_CHALLENGE_API_ACTIONS.create, (state: IScorecardState, { data }) => ({
    ...state,
    statementChallenge: statementChallengeAdapter.setOne(new ApiState().load(data), state.statementChallenge),
  })),
  on(STATEMENT_CREATE_CHALLENGE_API_ACTIONS.error, (state: IScorecardState, { statementId }) => ({
    ...state,
    statementChallenge: statementChallengeAdapter.mapOne(
      {
        id: statementId,
        map: (challengeApiState) =>
          challengeApiState.setError(null, challengeApiState.data as IStatementChallengeCreateRequest),
      },
      state.statementChallenge,
    ),
  })),
  on(STATEMENT_CREATE_CHALLENGE_API_ACTIONS.success, (state: IScorecardState, { data }) => ({
    ...state,
    statementChallenge: statementChallengeAdapter.removeOne(data.id, state.statementChallenge),
    scorecardDetails: state.scorecardDetails.success(
      getUpdatedScorecardDetailsByStatement(state.scorecardDetails.data!, data),
    ),
  })),

  on(STATEMENT_CHALLENGE_UPDATE_API_ACTIONS.update, (state, { challengeId, operation, comment }) => ({
    ...state,
    statementChallengeUpdating: statementChallengeUpdatingAdapter.setOne(
      new ApiState().update({ operation, id: challengeId, comment }),
      state.statementChallengeUpdating,
    ),
  })),
  on(STATEMENT_CHALLENGE_UPDATE_API_ACTIONS.success, (state, { data, challengeId }) => ({
    ...state,
    scorecardDetails: state.scorecardDetails.success(
      getUpdatedScorecardDetailsByStatement(state.scorecardDetails.data!, data),
    ),
    statementChallengeUpdating: statementChallengeUpdatingAdapter.removeOne(
      challengeId,
      state.statementChallengeUpdating,
    ),
  })),
  on(STATEMENT_CHALLENGE_UPDATE_API_ACTIONS.error, (state, { challengeId }) => ({
    ...state,
    statementChallengeUpdating: statementChallengeUpdatingAdapter.mapOne(
      {
        id: challengeId,
        map: (challenge) => challenge.setError(null, challenge.data as IStatementChallengeUpdateRequest),
      },
      state.statementChallengeUpdating,
    ),
  })),

  on(SCORECARD_CHALLENGE_API_ACTIONS.execute, (state: IScorecardState, { operation }) => ({
    ...state,
    scorecardChallenge: state.scorecardChallenge.update(operation),
  })),
  on(SCORECARD_CHALLENGE_API_ACTIONS.error, (state: IScorecardState) => ({
    ...state,
    scorecardChallenge: state.scorecardChallenge.setError(
      null,
      state.scorecardChallenge.data as TScorecardChallengeOperation,
    ),
  })),
  on(SCORECARD_CHALLENGE_API_ACTIONS.success, (state: IScorecardState, { data }) => ({
    ...state,
    scorecardChallenge: state.scorecardChallenge.success(state.scorecardChallenge.data as TScorecardChallengeOperation),
    scorecardDetails: state.scorecardDetails.success(data),
  })),
);

export const SCORECARD_REDUCER = createFeature({
  name: SCORECARD_STATE_KEY,
  reducer,
  extraSelectors: ({ selectScorecardState, selectScorecardPreview }) => ({
    selectScorecardDetailsNormalized: createSelector(selectScorecardState, ({ scorecardPreview, scorecardDetails }) => {
      const previewData = scorecardPreview.data?.find(
        ({ scorecardId }) => scorecardId === scorecardDetails.data?.scorecardId,
      );

      return !previewData || !scorecardDetails.data
        ? null
        : new ScorecardDetailsNormalizedModel({
            ...scorecardDetails.data,
            ...previewData,
          });
    }),
    selectDisqualifiedScorecardPreviews: (disqualified: boolean = false): MemoizedSelector<any, any> =>
      createSelector(selectScorecardPreview, ({ data }): IScorecardPreview[] =>
        (data || []).filter((preview) => disqualified === preview.disqualified),
      ),
    selectStatements: createSelector(
      selectScorecardState,
      ({ scorecardDetails: { data } }): IStatement[] =>
        data?.sections.reduce((statements, section) => statements.concat(section.statements), [] as IStatement[]) || [],
    ),
  }),
});

export const getUpdatedScorecardDetailsByStatement = (
  scorecardDetails: IScorecard,
  updatedStatement: IStatement,
): IScorecard => ({
  ...scorecardDetails,
  sections: scorecardDetails?.sections.map((section) => ({
    ...section,
    statements: section.statements.map((statement) =>
      statement.id === updatedStatement.id ? updatedStatement : statement,
    ),
  })),
});
